/**
 * IP-based location data — ported from v1.
 *
 * Fetches approximate location from ipapi.co.
 * Used for:
 * - loginLocation: set once when device first registers
 * - lastSeenLocation: updated every 24h
 *
 * Returns a JSON string with { city, region, country, country_name, timezone }.
 * Returns undefined on failure (non-blocking).
 */

import { Capacitor } from "@capacitor/core"

export interface LocationFields {
  city?: string
  region?: string
  country?: string
  country_name?: string
  timezone?: string
}

export async function fetchLocationData(): Promise<string | undefined> {
  try {
    // On native, prefer CapacitorHttp to bypass CORS
    if (Capacitor.isNativePlatform()) {
      try {
        const { CapacitorHttp } = await import("@capacitor/core")
        const response = await Promise.race([
          CapacitorHttp.get({ url: "https://ipapi.co/json/" }),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("timeout")), 5000),
          ),
        ])
        if (response.status === 200 && response.data) {
          const filtered: LocationFields = {
            city: response.data.city,
            region: response.data.region,
            country: response.data.country,
            country_name: response.data.country_name,
            timezone: response.data.timezone,
          }
          return JSON.stringify(filtered)
        }
        return undefined
      } catch {
        // Fall through to fetch
      }
    }

    // Web fallback — use plain fetch (no custom headers) to avoid CORS issues
    // with third-party APIs that don't allow our request-id header.
    const response = await fetch("https://ipapi.co/json/", {
      signal: AbortSignal.timeout(5000),
    })
    if (response.ok) {
      const data = await response.json()
      const filtered: LocationFields = {
        city: data.city,
        region: data.region,
        country: data.country,
        country_name: data.country_name,
        timezone: data.timezone,
      }
      return JSON.stringify(filtered)
    }
    return undefined
  } catch {
    console.warn("[LocationData] Failed to fetch location from ipapi.co")
    return undefined
  }
}
